import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'SavedViewsMenu' }

import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue'
import { useSavedFilters } from '@/composables/useSavedFilters'
import { openSavedViews } from '@/composables/useSavedViewsDrawer'
import type { SavedFilterRow } from '@/util/savedFiltersApi'

// Standalone saved-views chip for pages that do NOT use FilterBar (e.g. the bookings
// views). The page keeps its own filter UI; this only adds the views menu.
//
// Contract:
//   - bind :current-filters to the page's serializable filter state (the object
//     that gets stored in a saved view). This component deep-watches it and
//     debounce-saves the "last used" row automatically.
//   - pass :url-values / :url-has-filters so precedence can prefer a deep link.
//   - handle @apply(values): set the page's filter state from a chosen/restored view.
//   - handle @ready(): run the page's initial fetch (fires once after precedence
//     resolves, so the page should defer its first filter fetch until then).

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    viewKey: null,
    currentFilters: null,
    urlValues: null,
    urlHasFilters: { type: Boolean }
  },
  emits: ["apply", "ready"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'apply', values: Record<string, any>): void
  (e: 'ready'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  viewKey: string
  currentFilters: Record<string, any>
  urlValues?: Record<string, any> | null
  urlHasFilters?: boolean
}





const saved = useSavedFilters(props.viewKey)
const presets = computed<SavedFilterRow[]>(() => saved.presets.value)

const viewsOpen = ref(false)
const savingPreset = ref(false)
const newViewName = ref('')
const appliedPresetId = ref<number | null>(null)

const currentViewLabel = computed(() => {
  if (appliedPresetId.value != null) {
    const p = presets.value.find((x) => x.id === appliedPresetId.value)
    if (p) return p.name
  }
  return 'Views'
})

const inst = getCurrentInstance()
const vm = inst ? (inst.proxy as any) : null
function readViewParam(): number | null {
  const v = vm?.$route?.query?.view
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}
function stripViewParam() {
  const router = vm?.$router
  const route = vm?.$route
  if (!router || !route || !('view' in route.query)) return
  const q = { ...route.query }
  delete q.view
  router.replace({ query: q }).catch(() => {})
}

let ready = false
// Set right before we emit an apply so the deep-watch keeps appliedPresetId
// (a preset was applied) rather than treating the change as a manual edit.
let applyingPreset = false

function applyPreset(p: SavedFilterRow) {
  applyingPreset = true
  appliedPresetId.value = p.id
  viewsOpen.value = false
  emit('apply', p.filters)
}

async function onSavePreset() {
  const name = newViewName.value.trim()
  if (!name) return
  savingPreset.value = true
  try {
    const id = await saved.savePreset(name, props.currentFilters)
    if (id != null) appliedPresetId.value = id
    newViewName.value = ''
    viewsOpen.value = false
  } finally {
    savingPreset.value = false
  }
}

async function toggleDefault(p: SavedFilterRow) {
  await saved.setDefault(p.id, !p.is_default)
}

async function onDeletePreset(p: SavedFilterRow) {
  await saved.remove(p.id)
  if (appliedPresetId.value === p.id) appliedPresetId.value = null
}

function onManageViews() {
  viewsOpen.value = false
  openSavedViews()
}

onMounted(async () => {
  await saved.load()
  const presetId = readViewParam()
  const res = saved.resolve({ presetId, urlValues: props.urlValues, urlHasFilters: props.urlHasFilters })
  if (res.values) {
    applyingPreset = true
    emit('apply', res.values)
  }
  appliedPresetId.value = res.appliedPresetId
  saved.primeLastUsedBaseline(res.values || props.currentFilters)
  if (presetId != null) stripViewParam()
  ready = true
  emit('ready')
})

// Persist the page's current filters as "last used" (debounced) on every change,
// and drop the applied-preset label once the user edits away from it.
watch(
  () => props.currentFilters,
  (val) => {
    if (!ready) return
    if (applyingPreset) applyingPreset = false
    else appliedPresetId.value = null
    saved.saveLastUsed(val)
  },
  { deep: true },
)

// Apply a preset when ?view= appears while already mounted (opened from the drawer
// on the page you're already on).
watch(
  () => vm?.$route?.query?.view,
  (v) => {
    if (!ready || v == null || v === '') return
    const n = Number(v)
    if (!Number.isFinite(n)) return
    const preset = presets.value.find((p) => p.id === n)
    stripViewParam()
    if (preset) applyPreset(preset)
  },
)

return { __sfc: true,props, emit, saved, presets, viewsOpen, savingPreset, newViewName, appliedPresetId, currentViewLabel, inst, vm, readViewParam, stripViewParam, ready, applyingPreset, applyPreset, onSavePreset, toggleDefault, onDeletePreset, onManageViews }
}

})